/**
 * World items: apples, the ball, grime blobs, the egg. Few (<= MAX_ITEMS),
 * stable u16 ids, swap-remove slots. Physics is deliberately toy-grade:
 * gravity + one soft bounce + friction.
 */
import { PARAMS } from './params';
import { terrainHeight } from './terrain';

export const ITEM = {
  APPLE: 1,
  BALL: 2,
  GRIME: 3,
  EGG: 4,
  // structures — throng-built automation, placed at trail hotspots
  FEEDER: 5, // apple tree: drops apples on its own
  SPRING: 6, // bath spring: clean regenerates nearby
  TOTEM: 7, // play totem: fun decays slower nearby
} as const;

export type ItemType = (typeof ITEM)[keyof typeof ITEM];

const GRAVITY = -14;

export class ItemStore {
  count = 0;
  private nextId = 1;
  readonly id = new Uint16Array(PARAMS.MAX_ITEMS);
  readonly type = new Uint8Array(PARAMS.MAX_ITEMS);
  readonly state = new Uint8Array(PARAMS.MAX_ITEMS); // apple: bites left; grime: 0-100; egg: cracks
  readonly x = new Float32Array(PARAMS.MAX_ITEMS);
  readonly z = new Float32Array(PARAMS.MAX_ITEMS);
  readonly y = new Float32Array(PARAMS.MAX_ITEMS); // absolute; ground = terrainHeight
  readonly vx = new Float32Array(PARAMS.MAX_ITEMS);
  readonly vz = new Float32Array(PARAMS.MAX_ITEMS);
  readonly vy = new Float32Array(PARAMS.MAX_ITEMS);
  readonly bounceCooldown = new Uint8Array(PARAMS.MAX_ITEMS); // ticks between bounce events
  readonly timer = new Uint16Array(PARAMS.MAX_ITEMS); // structure logic (feeder drop timer)

  spawn(type: ItemType, x: number, z: number, y?: number): number {
    if (this.count >= PARAMS.MAX_ITEMS) return -1;
    const i = this.count++;
    this.id[i] = this.nextId++;
    if (this.nextId > 65000) this.nextId = 1;
    this.type[i] = type;
    this.x[i] = x;
    this.z[i] = z;
    this.y[i] = y ?? terrainHeight(x, z);
    this.vx[i] = 0;
    this.vz[i] = 0;
    this.vy[i] = 0;
    this.timer[i] = 0; // slots are recycled — never inherit a previous life's clock
    this.bounceCooldown[i] = 0;
    this.state[i] =
      type === ITEM.APPLE ? PARAMS.APPLE_BITES : type === ITEM.GRIME ? 100 : 0;
    return i;
  }

  remove(slot: number): void {
    const last = this.count - 1;
    if (slot !== last) {
      this.id[slot] = this.id[last] as number;
      this.type[slot] = this.type[last] as number;
      this.state[slot] = this.state[last] as number;
      this.x[slot] = this.x[last] as number;
      this.z[slot] = this.z[last] as number;
      this.y[slot] = this.y[last] as number;
      this.vx[slot] = this.vx[last] as number;
      this.vz[slot] = this.vz[last] as number;
      this.vy[slot] = this.vy[last] as number;
      this.bounceCooldown[slot] = this.bounceCooldown[last] as number;
      this.timer[slot] = this.timer[last] as number;
    }
    this.count = last;
  }

  slotOfId(id: number): number {
    for (let i = 0; i < this.count; i++) if (this.id[i] === id) return i;
    return -1;
  }

  firstOfType(type: ItemType): number {
    for (let i = 0; i < this.count; i++) if (this.type[i] === type) return i;
    return -1;
  }

  countOfType(type: ItemType): number {
    let n = 0;
    for (let i = 0; i < this.count; i++) if (this.type[i] === type) n++;
    return n;
  }

  /** Nearest item of type within maxDist of (x, z); returns slot or -1. */
  nearestOfType(type: ItemType, x: number, z: number, maxDist: number): number {
    let best = -1;
    let bestD2 = maxDist * maxDist;
    for (let i = 0; i < this.count; i++) {
      if (this.type[i] !== type) continue;
      const dx = (this.x[i] as number) - x;
      const dz = (this.z[i] as number) - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    return best;
  }

  /** Integrate flying/rolling items. Returns bounce events via callback. */
  tickPhysics(dt: number, onBounce: (slot: number, speed: number) => void): void {
    for (let i = 0; i < this.count; i++) {
      const t = this.type[i];
      if (t !== ITEM.APPLE && t !== ITEM.BALL) continue;
      let vx = this.vx[i] as number;
      let vz = this.vz[i] as number;
      let vy = this.vy[i] as number;
      if (vx === 0 && vz === 0 && vy === 0) continue;
      if ((this.bounceCooldown[i] as number) > 0) {
        this.bounceCooldown[i] = (this.bounceCooldown[i] as number) - 1;
      }
      let x = (this.x[i] as number) + vx * dt;
      let z = (this.z[i] as number) + vz * dt;
      let y = (this.y[i] as number) + vy * dt;
      const ground = terrainHeight(x, z);
      // rolling: stay glued over terrain undulations instead of micro-falling
      if (vy <= 0 && y - ground < 0.12) {
        if (vy < -2.2 && this.bounceCooldown[i] === 0) {
          onBounce(i, -vy);
          this.bounceCooldown[i] = 8;
          y = ground;
          vy = -vy * (t === ITEM.BALL ? 0.55 : 0.3);
        } else {
          y = ground;
          vy = 0;
        }
        // ground friction
        const f = t === ITEM.BALL ? 0.96 : 0.75;
        vx *= f;
        vz *= f;
        if (vx * vx + vz * vz < 0.01) {
          vx = 0;
          vz = 0;
        }
      } else {
        vy += GRAVITY * dt;
      }
      // keep on the island
      const r = Math.hypot(x, z);
      const limit = PARAMS.ISLAND_RADIUS * 0.9;
      if (r > limit) {
        x *= limit / r;
        z *= limit / r;
        vx *= -0.5;
        vz *= -0.5;
      }
      this.x[i] = x;
      this.z[i] = z;
      this.y[i] = y;
      this.vx[i] = vx;
      this.vz[i] = vz;
      this.vy[i] = vy;
    }
  }
}
